import type { ClientPortalSummary } from '@/lib/api/client-portal.api'
import { clientPortalApi } from '@/lib/api/client-portal.api'
import {
  isNotificationReadUnavailable,
  NotificationReadContractError,
} from '@/modules/notifications/reads.api'

import type { ClientPortalSettingsValues } from '@scrambl/shared'
import { DEFAULT_CLIENT_PORTAL_SETTINGS, isValidUUID } from '@scrambl/shared'

export interface PortalMetadataContext {
  userId: string
  entityId: string
  signal: AbortSignal
}
export class PortalMetadataContractError extends NotificationReadContractError {
  constructor() {
    super()
    this.name = 'PortalMetadataContractError'
    this.message = 'Workspace is unavailable'
  }
}
const fail = (): never => {
  throw new PortalMetadataContractError()
}
const count = (value: unknown) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
function assertContext(context: PortalMetadataContext) {
  context.signal.throwIfAborted()
  if (![context.userId, context.entityId].every(isValidUUID)) fail()
}
export const portalMetadataKeys = {
  root: (entityId: string | null | undefined) =>
    ['client-portal', 'owned-metadata', entityId ?? null] as const,
  summary: (entityId: string | null | undefined, session: unknown) =>
    [...portalMetadataKeys.root(entityId), 'summary', session] as const,
  counts: (entityId: string | null | undefined, session: unknown) =>
    [...portalMetadataKeys.root(entityId), 'sidebar-counts', session] as const,
}
export const isPortalMetadataUnavailable = isNotificationReadUnavailable
export const portalMetadataApi = {
  async summary(context: PortalMetadataContext): Promise<ClientPortalSummary> {
    assertContext(context)
    const body = await clientPortalApi.getPortalSummary(context.entityId, {
      signal: context.signal,
    })
    context.signal.throwIfAborted()
    if (
      !body ||
      body.userId !== context.userId ||
      body.entity?.id !== context.entityId ||
      !isValidUUID(body.organization?.id) ||
      typeof body.isInternalViewer !== 'boolean' ||
      typeof body.entity.name !== 'string' ||
      typeof body.organization.name !== 'string' ||
      typeof body.organization.slug !== 'string' ||
      !body.settings ||
      !body.counts
    )
      fail()
    for (const key of Object.keys(DEFAULT_CLIENT_PORTAL_SETTINGS)) {
      if (typeof Reflect.get(body.settings, key) !== 'boolean') fail()
    }
    for (const key of ['projects', 'notes', 'threads', 'openTickets']) {
      if (!count(Reflect.get(body.counts, key))) fail()
    }
    if (
      (body.settings.showNotes === false && body.counts.notes !== 0) ||
      (body.settings.showDiscussions === false && body.counts.threads !== 0) ||
      (body.settings.showSupport === false && body.counts.openTickets !== 0)
    )
      fail()
    const textOrNull = (value: unknown) =>
      value == null || typeof value === 'string'
    if (!textOrNull(body.entity.logoUrl) || !textOrNull(body.organization.logo))
      fail()
    if (
      body.branding &&
      Object.values(body.branding).some(value => !textOrNull(value))
    )
      fail()
    if (
      body.ticketTypes &&
      (!Array.isArray(body.ticketTypes) ||
        body.ticketTypes.some(
          type =>
            !type ||
            !isValidUUID(type.id) ||
            typeof type.key !== 'string' ||
            typeof type.name !== 'string' ||
            !textOrNull(type.description) ||
            !textOrNull(type.icon) ||
            !textOrNull(type.color) ||
            (type.order != null && !Number.isSafeInteger(type.order)),
        ))
    )
      fail()
    const settings = Object.fromEntries(
      Object.keys(DEFAULT_CLIENT_PORTAL_SETTINGS).map(key => [
        key,
        Reflect.get(body.settings, key),
      ]),
    ) as ClientPortalSettingsValues
    const { entity, organization, counts, branding } = body
    // Explicit public projection excludes nested/raw and future internal fields.
    return {
      userId: body.userId,
      isInternalViewer: body.isInternalViewer,
      entity: { id: entity.id, name: entity.name, logoUrl: entity.logoUrl },
      organization: {
        id: organization.id,
        name: organization.name,
        logo: organization.logo,
        slug: organization.slug,
      },
      settings,
      counts: {
        projects: counts.projects,
        notes: counts.notes,
        threads: counts.threads,
        openTickets: counts.openTickets,
      },
      ticketTypes: body.ticketTypes?.map(type => ({
        id: type.id,
        key: type.key,
        name: type.name,
        description: type.description,
        icon: type.icon,
        color: type.color,
        order: type.order,
      })),
      branding: branding
        ? {
            logoUrl: branding.logoUrl,
            accentColor: branding.accentColor,
            sidebarColor: branding.sidebarColor,
            secondaryColor: branding.secondaryColor,
            coverImageUrl: branding.coverImageUrl,
            coverImagePosition: branding.coverImagePosition,
          }
        : undefined,
    }
  },
  async counts(context: PortalMetadataContext) {
    assertContext(context)
    const body = await clientPortalApi.getSidebarCounts(context.entityId, {
      signal: context.signal,
    })
    context.signal.throwIfAborted()
    if (
      !body ||
      body.userId !== context.userId ||
      body.entityId !== context.entityId ||
      !isValidUUID(body.organizationId) ||
      typeof body.isInternalViewer !== 'boolean' ||
      !count(body.unreadDocs) ||
      !count(body.unreadUpdates)
    )
      fail()
    return {
      userId: context.userId,
      entityId: context.entityId,
      organizationId: body.organizationId!,
      isInternalViewer: body.isInternalViewer!,
      unreadDocs: body.unreadDocs,
      unreadUpdates: body.unreadUpdates,
    }
  },
}
